import type { WorkbookCell, WorkbookSheet } from "./excel";

export type ExportColumn = { key: string; label: string; format?: "integers" | "decimals" | "percentagePoints" | "dates"; width?: number };
// The view owns visibility; the exporter owns raw (unrounded) values and formats.
export const exportVisibleColumns = new Map<string, string[]>();

export async function collectExportPages<T extends { label: string }>(
  load: (offset: number, limit: number) => Promise<{ rows: T[]; total_rows: number }>,
): Promise<T[]> {
  const rows: T[] = [], labels = new Set<string>();
  let expected: number | undefined;
  do {
    const page = await load(rows.length, 500);
    expected ??= page.total_rows;
    if (!Number.isSafeInteger(expected) || expected < 0 || page.total_rows !== expected || (!page.rows.length && rows.length < expected)) throw new Error("Excel satır kapsamı değişti. Tekrar deneyin.");
    for (const row of page.rows) {
      if (labels.has(row.label)) throw new Error("Excel verisinde tekrar eden satır bulundu.");
      labels.add(row.label); rows.push(row);
    }
    if (rows.length > expected) throw new Error("Excel satır sayısı eşleşmedi.");
  } while (rows.length < expected!);
  return rows;
}

export function tableExcelSheet(
  options: Omit<WorkbookSheet, "rows">,
  columns: ExportColumn[],
  records: Record<string, WorkbookCell>[],
  total?: Record<string, WorkbookCell>,
): WorkbookSheet {
  const visible = exportVisibleColumns.get(options.title ?? "");
  const selected = visible ? columns.filter(column => visible.includes(column.key)) : columns;
  const fields = selected.length ? selected : columns.slice(0, 1);
  const rows = [fields.map(column => column.label), ...(total ? [fields.map(column => total[column.key] ?? null)] : []), ...records.map(row => fields.map(column => row[column.key] ?? null))];
  const result: WorkbookSheet = {
    ...options, rows,
    rowKinds: ["header", ...(total ? ["total" as const] : []), ...records.map(() => "detail" as const)],
    widths: fields.map(column => column.width ?? (column.format ? 16 : 25)),
    freezeColumns: Math.min(options.freezeColumns ?? 1, fields.length),
  };
  for (const format of ["integers", "decimals", "percentagePoints", "dates"] as const) {
    result[format] = fields.flatMap((column, index) => column.format === format ? [index + 1] : []);
  }
  result.progressComparisons = options.progressComparisons?.flatMap(rule => {
    const key = columns[rule.column - 1]?.key;
    const index = fields.findIndex(column => column.key === key);
    return index < 0 ? [] : [{ ...rule, column: index + 1 }];
  });
  result.wrapColumns = options.wrapColumns?.flatMap(index => {
    const selectedIndex = fields.findIndex(column => column.key === columns[index - 1]?.key);
    return selectedIndex < 0 ? [] : [selectedIndex + 1];
  });
  return result;
}
